import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import { useStore } from '../../hooks/useStore';
import {
    getCasinoGameOfTheDayPaybackBooster,
    getPaybackBoosterProgramGames,
} from '../../microservices/casino-payback-booster';
import { useSocketSubscribeUnsubscribe, useSocketTopicEvents } from '../../microservices/pusher';
import { stores } from '../../stores';
import { casino } from '../../stores/casino';
import { paybackBooster as paybackBoosterStore } from '../../stores/payback-booster';
import { getStoreValue } from '../../stores/store/utils';
import { formattedAmountWithCurrency, getActiveCurrency } from '../currency';
import { logger } from '../logger';
import { showToast } from '../toast';
import { ToastType } from '../toast/types';
import { translate } from '../translate';
import { PaybackBooster, PaybackBoosterStatus, TransactionNotificationsTypes } from './types';

const sentNotifications: string[] = [];

export function usePaybackBoosterTransactionNotifications() {
    const [isAuthenticated] = useStore(stores.isAuthenticated);
    useSocketSubscribeUnsubscribe('casino-payback-booster-transaction-notification', {
        guardFunction: () => isAuthenticated,
        params: [],
        watchParams: [isAuthenticated],
    });
    useSocketTopicEvents('casino-payback-booster-transaction-notification', onMessage);
}

export async function loadPaybackBoosterProgramGames(): Promise<void> {
    try {
        const isAuthenticated = getStoreValue(stores.isAuthenticated);
        let paybackBooster = getStoreValue(paybackBoosterStore.paybackBooster);

        if (isAuthenticated) {
            if (isEmpty(paybackBooster)) {
                paybackBooster = await getCasinoGameOfTheDayPaybackBooster();
            }

            const isPaybackBooster = paybackBooster.amountTotal !== undefined;
            const isExpired = Boolean(new Date(paybackBooster.expiryDate) < new Date());
            if (isPaybackBooster) {
                paybackBoosterStore.paybackBooster.set(isExpired ? undefined : paybackBooster);
            }
            if (!isEmpty(paybackBooster) && paybackBooster.programId) {
                const program = await getPaybackBoosterProgramGames([paybackBooster.programId]);

                if (program) {
                    paybackBoosterStore.programGames.set(program);
                }
            }
        }
    } catch (error) {
        logger.error('CasinoPaybackBoosterService', 'loadPaybackBoosterProgramGames', error);
    }
}

export function usePaybackBoosterProgram(serverGameId?: string) {
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated] = useStore(stores.isAuthenticated);
    const [paybackBooster, setPaybackBooster] = useStore(paybackBoosterStore.paybackBooster);
    const [isSecondaryDataInitialized] = useStore(casino.isSecondaryDataInitialized);

    useSocketSubscribeUnsubscribe('casino-payback-booster-payback-booster-update', {
        guardFunction: () => isAuthenticated,
        params: [],
        watchParams: [isAuthenticated],
    });
    useSocketSubscribeUnsubscribe('casino-payback-booster-turnover-update', {
        guardFunction: () => isAuthenticated,
        params: [],
        watchParams: [isAuthenticated],
    });

    useEffect(() => {
        if (isAuthenticated && (isEmpty(paybackBooster) || serverGameId) && isSecondaryDataInitialized) {
            loadPaybackBoosterProgramGames();
        }
        setIsLoading(false);
    }, [isAuthenticated, serverGameId]);

    useSocketTopicEvents(
        'casino-payback-booster-payback-booster-update',
        (message: { userId: string; paybackBooster: PaybackBooster }) => {
            const { paybackBooster } = message;
            if (paybackBooster.status === PaybackBoosterStatus.active) {
                return setPaybackBooster(paybackBooster);
            }
        },
    );

    return {
        isLoading,
        paybackBooster,
        setPaybackBooster,
        showMessage,
    };
}

function onMessage({ type, data }: { type: TransactionNotificationsTypes; data?: any }) {
    const currency = getActiveCurrency();
    if (type === TransactionNotificationsTypes.BET_OUT_OF_RANGE && data) {
        const { minBet, maxBet } = data.turnoverSettings || data.cashbackSettings;
        showMessage(
            {
                text: `The bet range for this Payback Booster program is between %1 and %2`,
                params: [formattedAmountWithCurrency(minBet[currency]), formattedAmountWithCurrency(maxBet[currency])],
            },
            type,
        );
    } else if (type === TransactionNotificationsTypes.MAX_RETURN_AMOUNT_REACHED) {
        showMessage(
            {
                text: `You have reached the maximum payback amount set for this Payback Booster program`,
            },
            type,
        );
    } else if (type === TransactionNotificationsTypes.NOT_RIGHT_GAME) {
        showMessage({ text: `This game is not eligible for the current Payback Booster program` }, type);
    } else if (type === TransactionNotificationsTypes.HAS_ACTIVE_CASINO_BONUS) {
        showMessage(
            {
                text: `Your bets made with an active Casino Bonus will not count towards the Payback Booster program`,
            },
            type,
        );
    } else if (type === TransactionNotificationsTypes.BALANCE_TRANSFERRED && data) {
        const { amount } = data;
        showMessage(
            {
                title: 'Congratulations!',
                text: translate('balance-transferred', 'casino.payback'),
                type: 'success',
                params: [formattedAmountWithCurrency(amount)],
            },
            type,
        );
    }
}

function showMessage(
    {
        text,
        title = `Your bet is not applicable`,
        type,
        params,
    }: {
        text: string;
        title?: string;
        type?: ToastType;
        params?: string[];
    },
    transactionNotificationType: TransactionNotificationsTypes,
) {
    if (!sentNotifications.includes(transactionNotificationType)) {
        showToast({
            title: translate(title, 'ui.casino'),
            text: translate(text, 'casino.payback-booster', params),
            size: 'size-small',
            iconName: 'payback-booster',
            type: type || 'warning',
            closeOnLogout: true,
            lifespan: 6000,
        });
        sentNotifications.push(transactionNotificationType);
    }
}
